import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import Logo from "../components/Logo";
import { Search, User, MapPin, MessageSquare, Users, Loader2, List } from "lucide-react";

// Guest-facing seat finder — the destination for the printed QR code.
// UX: single search box, one clear result, no leaking other guests' seats.
// Host can optionally flip on a public directory (browsable list + layout photo).
const SeatFinderPage = () => {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState("");
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirMode, setDirMode] = useState(false);
  const [directory, setDirectory] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/events/${slug}/seat/meta`);
        if (!cancel) setMeta(r.data);
      } catch (e) {
        if (!cancel) setMetaErr(e?.response?.data?.detail || "Seat finder not available for this event.");
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const doLookup = async (e) => {
    e?.preventDefault?.();
    if ((q || "").trim().length < 2) return;
    setBusy(true); setResult(null);
    try {
      const r = await axios.get(`${API}/public/events/${slug}/seat/lookup?q=${encodeURIComponent(q.trim())}`);
      setResult(r.data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || "Something went wrong. Try again." });
    } finally { setBusy(false); }
  };

  const loadDirectory = async () => {
    setDirMode(true); setDirectory(null);
    try {
      const r = await axios.get(`${API}/public/events/${slug}/seat/directory`);
      setDirectory(r.data);
    } catch (err) {
      setDirectory({ error: err?.response?.data?.detail || "Directory not available." });
    }
  };

  if (metaErr) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="flex justify-center mb-4"><Logo size="sm" /></div>
          <p className="text-navy-700/70 text-sm">{metaErr}</p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-coral-300" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 py-12 px-5" data-testid="seat-finder-page">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><Logo size="sm" /></div>
        <div className="bg-white rounded-3xl soft-shadow p-8">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase text-center">Find your seat</p>
          <h1 className="font-serif italic text-navy-700 text-3xl md:text-4xl text-center mt-2">{meta.event_name}</h1>
          <p className="text-navy-700/50 text-sm text-center mt-1 capitalize">{meta.event_type || ""}</p>

          {!dirMode && (
            <>
              <form onSubmit={doLookup} className="mt-8 flex gap-2">
                <div className="relative flex-1">
                  <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                  <input
                    autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="seat-search-input"
                    placeholder="Type your name…"
                    className="w-full pl-10 pr-4 py-3 rounded-full border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
                    maxLength={80}
                  />
                </div>
                <button type="submit" disabled={busy || q.trim().length < 2}
                  data-testid="seat-search-submit"
                  className="inline-flex items-center gap-1.5 bg-coral-300 text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-50 whitespace-nowrap">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {busy ? "" : "Find"}
                </button>
              </form>

              {result?.match && (
                <div data-testid="seat-result-card" className="mt-6 bg-gradient-to-br from-coral-100 to-cream-100 rounded-2xl p-6 text-center border border-coral-200">
                  <p className="tracking-widest text-coral-500 text-[11px] font-semibold uppercase">You're seated</p>
                  <p className="font-serif italic text-navy-700 text-2xl mt-1">Welcome, {result.match.name}</p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl p-4">
                      <MapPin className="w-4 h-4 mx-auto text-coral-300" />
                      <div className="text-[10px] uppercase tracking-wider text-navy-700/50 mt-1.5">Table</div>
                      <div className="font-bold text-navy-700 text-2xl mt-0.5">{result.match.table || "—"}</div>
                    </div>
                    <div className="bg-white rounded-xl p-4">
                      <User className="w-4 h-4 mx-auto text-coral-300" />
                      <div className="text-[10px] uppercase tracking-wider text-navy-700/50 mt-1.5">Seat</div>
                      <div className="font-bold text-navy-700 text-2xl mt-0.5">{result.match.seat || "—"}</div>
                    </div>
                  </div>
                  {result.match.plus_ones > 0 && (
                    <div className="mt-3 text-xs text-navy-700/60 inline-flex items-center gap-1 bg-white/70 rounded-full px-3 py-1">
                      <Users className="w-3.5 h-3.5" /> +{result.match.plus_ones} guest{result.match.plus_ones === 1 ? "" : "s"}
                    </div>
                  )}
                  {result.match.notes && (
                    <div className="mt-3 text-sm text-navy-700/70 bg-white rounded-md px-3 py-2 flex items-start gap-2 text-left">
                      <MessageSquare className="w-4 h-4 mt-0.5 text-coral-300 shrink-0" />
                      <span>{result.match.notes}</span>
                    </div>
                  )}
                </div>
              )}
              {result && !result.match && (
                <div data-testid="seat-result-empty" className="mt-6 bg-cream-100 rounded-2xl p-5 text-center">
                  <p className="text-navy-700/70 text-sm">{result.hint || result.error}</p>
                  {result.candidates?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm text-navy-700">
                      {result.candidates.map((c, i) => (
                        <li key={i} className="bg-white rounded-md px-3 py-1.5">{c.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {meta.public_directory && (
                <button onClick={loadDirectory}
                  data-testid="seat-directory-open"
                  className="mt-6 w-full inline-flex items-center justify-center gap-1.5 text-navy-700/70 hover:text-coral-300 text-sm underline">
                  <List className="w-4 h-4" /> Browse the full guest list
                </button>
              )}
            </>
          )}

          {dirMode && (
            <div data-testid="seat-directory">
              <div className="mt-6 flex items-center justify-between">
                <button onClick={() => setDirMode(false)} className="text-xs text-navy-700/60 hover:text-coral-300">← Back to search</button>
                <span className="text-xs text-navy-700/50">{directory?.total ? `${directory.total} guests` : ""}</span>
              </div>
              {meta.layout_image_url && (
                <img src={meta.layout_image_url} alt="Table layout"
                  className="mt-3 w-full rounded-xl border border-cream-200"
                  data-testid="seat-layout-image" />
              )}
              {!directory && <div className="mt-6 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-coral-300" /></div>}
              {directory?.error && <div className="mt-6 bg-red-50 text-red-700 text-sm rounded-md px-3 py-2">{directory.error}</div>}
              {directory?.guests && (
                <div className="mt-4 max-h-[60vh] overflow-auto divide-y divide-cream-200">
                  {directory.guests.map((g, i) => (
                    <div key={i} className="py-3 flex justify-between items-center text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-navy-700 truncate">{g.name}</div>
                        {g.notes && <div className="text-[11px] text-navy-700/50 truncate">{g.notes}</div>}
                      </div>
                      <div className="text-right text-navy-700/70 text-xs whitespace-nowrap">
                        {g.table ? <>Table <b>{g.table}</b></> : "—"}
                        {g.seat ? <> · Seat <b>{g.seat}</b></> : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-navy-700/50 text-center mt-6">
          Powered by <a href="/" className="text-coral-300 hover:underline">GalerieCop</a>
        </p>
      </div>
    </div>
  );
};

export default SeatFinderPage;
